/**
 * YLI GA4 Collection Featured Cards Tracking
 * Version: 1.0.0
 * Created: 2026-01-14
 * Purpose: Track featured card views and interactions
 * GA4 Events: collection_featured_card_view, collection_featured_card_click, collection_cta_click
 * Spec: docs/ga4-events/ga4-event-tracking-plan.md
 */

(function() {
  'use strict';

  // Session storage key for tracking viewed cards
  var VIEWED_CARDS_KEY = 'yli_ga4_viewed_cards';

  /**
   * Get collection handle from URL
   * @returns {string} Collection handle (e.g., 'majestic', 'dream-seam')
   */
  function getCollectionName() {
    var pathname = window.location.pathname;
    var match = pathname.match(/\/collections\/([^\/]+)/);
    return match ? match[1] : 'unknown';
  }

  /**
   * Get set of viewed cards from sessionStorage
   * @returns {Set} Set of card IDs that have been viewed
   */
  function getViewedCards() {
    try {
      var stored = sessionStorage.getItem(VIEWED_CARDS_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch (e) {
      return new Set();
    }
  }

  /**
   * Save viewed card ID to sessionStorage
   * @param {string} cardId - Unique identifier for the card
   */
  function markCardAsViewed(cardId) {
    try {
      var viewedCards = getViewedCards();
      viewedCards.add(cardId);
      sessionStorage.setItem(VIEWED_CARDS_KEY, JSON.stringify(Array.from(viewedCards)));
    } catch (e) {
      // sessionStorage not available or quota exceeded
    }
  }

  /**
   * Extract card position from block ID or class
   * @param {Element} element - The featured card element
   * @returns {string} Card position (e.g., 'card_1', 'card_2')
   */
  function getCardPosition(element) {
    var blockId = element.id;
    if (blockId && blockId.includes('featured_image')) {
      return 'card_1'; // Default for metafield-based cards
    }

    // Check for data attribute or class that might indicate position
    var positionAttr = element.getAttribute('data-card-position');
    if (positionAttr) {
      return positionAttr;
    }

    return 'card_1'; // Default
  }

  /**
   * GA4 Event: collection_featured_card_view
   * Track when featured card becomes visible in viewport
   * Uses IntersectionObserver with 50% visibility threshold
   */
  function trackFeaturedCardViews() {
    // Check if IntersectionObserver is supported
    if (!('IntersectionObserver' in window)) {
      return; // Skip tracking on unsupported browsers
    }

    // Find all featured card elements
    var featuredCards = document.querySelectorAll('.product-item--featured-image');
    if (featuredCards.length === 0) {
      return; // No featured cards on this page
    }

    var collectionName = getCollectionName();
    var viewedCards = getViewedCards();

    // Create observer with 50% visibility threshold
    var observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        // Check if element is at least 50% visible
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          var cardElement = entry.target;
          var cardPosition = getCardPosition(cardElement);

          // Create unique ID for this card view
          var cardId = collectionName + '_' + cardPosition;

          // Only track if not already viewed in this session
          if (!viewedCards.has(cardId)) {
            // Get card heading text
            var headingElement = cardElement.querySelector('h2, h3, h4, .heading-medium');
            var cardHeading = headingElement ? headingElement.textContent.trim() : 'Unknown';

            // Fire GA4 event
            if (typeof gtag === 'function') {
              gtag('event', 'collection_featured_card_view', {
                'collection_name': collectionName,
                'card_heading': cardHeading,
                'card_position': cardPosition
              });
            }

            // Mark as viewed to prevent duplicate tracking
            markCardAsViewed(cardId);
            viewedCards.add(cardId);

            // Stop observing this element
            observer.unobserve(cardElement);
          }
        }
      });
    }, {
      threshold: 0.5 // Trigger when 50% of element is visible
    });

    // Observe all featured cards
    featuredCards.forEach(function(card) {
      observer.observe(card);
    });
  }

  /**
   * GA4 Event: collection_featured_card_click
   * Track clicks on card image, heading, or text area (not CTA button)
   */
  function trackFeaturedCardClicks() {
    document.addEventListener('click', function(e) {
      // Check if click is on a featured card element
      var cardElement = e.target.closest('.product-item--featured-image');
      if (!cardElement) {
        return; // Not a featured card click
      }

      // Ignore clicks on CTA buttons (tracked separately)
      if (e.target.closest('.btn, a.btn')) {
        return; // This is a CTA click, not a card click
      }

      // Check if this is a clickable card link
      var cardLink = e.target.closest('.product-item__image-link');
      if (!cardLink) {
        return; // Not a clickable area
      }

      var collectionName = getCollectionName();
      var cardPosition = getCardPosition(cardElement);

      // Get card heading
      var headingElement = cardElement.querySelector('h2, h3, h4, .heading-medium');
      var cardHeading = headingElement ? headingElement.textContent.trim() : 'Unknown';

      // Determine what was clicked
      var clickElement = 'image'; // Default
      if (e.target.closest('h2, h3, h4, .heading-medium')) {
        clickElement = 'heading';
      } else if (e.target.closest('p, .rte, .product-item__image-content')) {
        clickElement = 'text';
      }

      // Fire GA4 event
      if (typeof gtag === 'function') {
        gtag('event', 'collection_featured_card_click', {
          'collection_name': collectionName,
          'card_heading': cardHeading,
          'click_element': clickElement
        });
      }
    });
  }

  /**
   * GA4 Event: collection_cta_click
   * Track clicks on featured card CTA buttons
   */
  function trackCTAClicks() {
    document.addEventListener('click', function(e) {
      // Check if click is on a button within a featured card
      var buttonElement = e.target.closest('.product-item--featured-image .btn');
      if (!buttonElement) {
        return; // Not a featured card CTA button
      }

      var cardElement = buttonElement.closest('.product-item--featured-image');
      var collectionName = getCollectionName();

      // Get button text and URL
      var buttonText = buttonElement.querySelector('span')
        ? buttonElement.querySelector('span').textContent.trim()
        : buttonElement.textContent.trim();
      var buttonUrl = buttonElement.getAttribute('href') || '';

      // Get card heading for context
      var headingElement = cardElement.querySelector('h2, h3, h4, .heading-medium');
      var cardHeading = headingElement ? headingElement.textContent.trim() : 'Unknown';

      // Fire GA4 event
      if (typeof gtag === 'function') {
        gtag('event', 'collection_cta_click', {
          'collection_name': collectionName,
          'button_text': buttonText,
          'button_url': buttonUrl,
          'card_heading': cardHeading
        });
      }
    });
  }

  /**
   * Initialize all tracking functions when DOM is ready
   */
  function initializeTracking() {
    // Only run on collection pages
    if (!window.location.pathname.includes('/collections/')) {
      return;
    }

    trackFeaturedCardViews();
    trackFeaturedCardClicks();
    trackCTAClicks();
  }

  // Initialize tracking when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeTracking);
  } else {
    initializeTracking();
  }

})();
